import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { NoteLine } from "@/components/shared/NoteLine";
import BaseGarmentChangesRow from "./BaseGarmentChangesRow";
import BulkImportExpandedRow from "./BulkImportExpandedRow";
import ClientPriceChangesRow from "./ClientPriceChangesRow";
import SkuEditChangesRow from "./SkuEditChangesRow";
import UndoConfirmDialog from "./UndoConfirmDialog";

const PAGE_SIZE = 20;

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case "completed":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100";
    case "partially_undone":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100";
    case "undone":
      return "bg-slate-100 text-slate-700 dark:bg-slate-800/30 dark:text-slate-400 hover:bg-slate-100";
    default:
      return "bg-slate-100 text-slate-700 dark:bg-slate-800/30 dark:text-slate-400 hover:bg-slate-100";
  }
};

const formatStatusLabel = (status: string) => {
  switch (status) {
    case "completed":
      return "Completed";
    case "partially_undone":
      return "Partially Undone";
    case "undone":
      return "Undone";
    default:
      return status;
  }
};

interface BulkImportsTabProps {
  organizationId: string;
  locationId?: string;
  /** 3PL per-client filter (server-side, validated against accessible orgs). */
  clientOrgId?: string;
  /** Render the derived Clients column (shared 3PL locations only). */
  showClientColumn?: boolean;
  /** Resolver for owner-org display names (own org / connected clients / hex tag). */
  ownerOrgLabel?: (id: string | null | undefined, name: string | null | undefined) => string;
}

const BulkImportsTab = ({
  organizationId,
  locationId,
  clientOrgId,
  showClientColumn = false,
  ownerOrgLabel,
}: BulkImportsTabProps) => {
  const { t } = useTranslation("inventory");
  const { format } = useCurrencyFormatter();
  const { toast } = useToast();
  const utils = trpc.useUtils();

  const [page, setPage] = useState(1);
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());

  // A later page under one filter may not exist under the next — reset so a
  // client/location switch never lands on a phantom empty page.
  useEffect(() => {
    setPage(1);
  }, [clientOrgId, locationId]);
  const [undoBatchTarget, setUndoBatchTarget] = useState<{
    batchId: string;
    itemCount: number;
    totalQuantity: number;
    filename: string | null;
    importType: string;
  } | null>(null);

  const batchesQuery = trpc.logs.getBulkImportBatches.useQuery(
    {
      locationId,
      clientOrgId,
      page,
      limit: PAGE_SIZE,
    },
    {
      staleTime: 30_000,
    }
  );

  const undoCallbacks = {
    // The backend reports per-row `errors` AND a `fullyUndone` flag — the
    // flag also covers rows this caller cannot see (a revoked client
    // junction filters them out before the per-row loop, so they produce
    // no error yet remain applied). Never claim a full reversal off
    // errors[] alone.
    onSuccess: (result?: {
      undoneCount?: number;
      errors?: Array<unknown>;
      fullyUndone?: boolean;
      remainingCount?: number | null;
    }) => {
      const failed = result?.errors?.length ?? 0;
      if (failed > 0 || result?.fullyUndone === false) {
        // remainingCount null = unknown (count unavailable) — fall back to
        // the vague phrasing rather than inventing a number.
        const remainPhrase =
          typeof result?.remainingCount === "number" && result.remainingCount > 0
            ? `${result.remainingCount} row${result.remainingCount === 1 ? "" : "s"} remain applied`
            : "some rows remain applied";
        toast({
          title: "Undo partially completed",
          description: `${result?.undoneCount ?? 0} rows reversed; ${remainPhrase}${failed > 0 ? ` (${failed} failed)` : ""}. The document stays partially undone and can be retried.`,
          variant: "destructive",
        });
      } else {
        toast({ title: "Undo successful", description: "All stock changes from this batch have been reversed." });
      }
      void utils.logs.getBulkImportBatches.invalidate();
      void utils.logs.getBulkImportBatchDetail.invalidate();
      void utils.logs.getStockMovements.invalidate();
      setUndoBatchTarget(null);
    },
    onError: (error: { message: string }) => {
      toast({
        title: "Undo failed",
        description: error.message,
        variant: "destructive" as const,
      });
    },
  };

  const undoAddStockMutation = trpc.inventory.undoBulkImport.useMutation(undoCallbacks);
  const undoStocktakeMutation = trpc.inventory.undoBulkStocktake.useMutation(undoCallbacks);

  const isUndoing = undoAddStockMutation.isPending || undoStocktakeMutation.isPending;

  const handleUndoBatch = () => {
    if (!undoBatchTarget || !locationId) return;
    const payload = { locationId, batchId: undoBatchTarget.batchId };
    // A Base garments upload SET stock, like a stocktake: undo puts each SKU
    // back to its level before (and refuses through undoBulkImport).
    if (undoBatchTarget.importType === 'stocktake' || undoBatchTarget.importType === 'base_garment') {
      undoStocktakeMutation.mutate(payload);
    } else {
      undoAddStockMutation.mutate(payload);
    }
  };

  const toggleExpanded = (batchId: string) => {
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  const rows = batchesQuery.data?.data ?? [];
  const pagination = batchesQuery.data?.pagination;
  const batchColSpan = showClientColumn ? 10 : 9;

  return (
    <div className="space-y-4">
      {/* The table's own scroll box is a size container, so an expanded row
          can size itself to the part of the table that is on screen (cqw). */}
      <div className="bg-card rounded-lg shadow border [&>div]:[container-type:inline-size]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[56px]">Details</TableHead>
              <TableHead className="w-[180px]">Date/Time</TableHead>
              <TableHead className="w-[160px]">User</TableHead>
              <TableHead className="w-[160px] hidden sm:table-cell">Location</TableHead>
              {showClientColumn && <TableHead className="w-[160px]">Clients</TableHead>}
              <TableHead className="w-[80px] text-right">Items</TableHead>
              <TableHead className="w-[100px] text-right">Total Qty</TableHead>
              <TableHead className="w-[120px] text-right hidden sm:table-cell">Total Value</TableHead>
              <TableHead className="w-[140px]">Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batchesQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={batchColSpan} className="py-10 text-center text-muted-foreground">
                  Loading adjustments...
                </TableCell>
              </TableRow>
            ) : batchesQuery.error ? (
              <TableRow>
                <TableCell colSpan={batchColSpan} className="py-10 text-center text-destructive">
                  {batchesQuery.error.message}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={batchColSpan} className="py-10 text-center text-muted-foreground">
                  No adjustment documents found. Multi-SKU adjustments (CSV imports, stocktakes, bulk adds) will appear here.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const expanded = expandedBatches.has(row.id);
                // Org-level Products bulk edit or client price import: no
                // location, no stock change, never undoable.
                const isOrgLevel =
                  row.importType === "sku_edit" || row.importType === "client_price";
                // Undo services gate on organization_id = caller, so a
                // header another accessible org created (client-run import
                // at a shared floor) must not offer Undo — it would always
                // fail with "Import batch not found".
                // A Base garments upload with no stock lines has nothing to
                // undo (its codes, SKUs and settings stay; only stock undoes).
                const isBaseGarment = row.importType === "base_garment";
                const canUndo =
                  !isOrgLevel &&
                  row.status !== "undone" &&
                  row.ownerOrganizationId === organizationId &&
                  (!isBaseGarment || row.rowOrgs.length > 0);

                return (
                  <Fragment key={row.id}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => toggleExpanded(row.id)}
                    >
                      <TableCell>
                        {expanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {formatDateTime(row.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex flex-col gap-0.5">
                          <span>{row.createdByName ?? "System"}</span>
                          {row.reference ? (
                            <span
                              className="text-xs font-mono text-primary truncate max-w-[140px]"
                              title={row.reference}
                            >
                              {row.reference}
                            </span>
                          ) : null}
                          {row.filename ? (
                            <span className="text-xs text-muted-foreground truncate max-w-[140px]" title={row.filename}>
                              {row.filename}
                            </span>
                          ) : null}
                          {row.importType === 'stocktake' && (
                            <Badge variant="success" size="sm" className="w-fit">
                              Stocktake
                            </Badge>
                          )}
                          {row.importType === "sku_edit" && (
                            <Badge variant="outline" size="sm" className="w-fit">
                              Bulk edit
                            </Badge>
                          )}
                          {row.importType === "client_price" && (
                            <Badge variant="outline" size="sm" className="w-fit">
                              {t("clientPrices.logs.badge")}
                            </Badge>
                          )}
                          {isBaseGarment && (
                            <Badge variant="outline" size="sm" className="w-fit">
                              {t("baseGarments.logs.badge")}
                            </Badge>
                          )}
                          {row.importType === 'bulk_adjust' && (
                            <Badge variant="outline" size="sm" className="w-fit">
                              {/* One direction per document, so the header
                                  total's sign IS the direction. Undo zeroes
                                  the total, so 0 gets the neutral label. */}
                              {row.totalQuantityChange < 0
                                ? 'Bulk Consume'
                                : row.totalQuantityChange > 0
                                  ? 'Bulk Add'
                                  : 'Bulk Adjust'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                        {row.locationId === null ? "All locations" : row.locationName ?? row.locationId}
                      </TableCell>
                      {showClientColumn && (
                        <TableCell className="text-sm text-muted-foreground">
                          <div className="flex flex-wrap gap-1">
                            {row.rowOrgs.length > 0 ? (
                              row.rowOrgs.map((o) => (
                                <Badge key={o.id} variant="outline" size="sm">
                                  {ownerOrgLabel ? ownerOrgLabel(o.id, o.name) : o.name ?? o.id.slice(0, 8)}
                                </Badge>
                              ))
                            ) : (
                              <span>-</span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-right tabular-nums">
                        {row.successfulCount}
                        {row.failedCount > 0 ? (
                          <span className="text-red-600 ml-1 text-xs">
                            ({row.failedCount} {isOrgLevel ? "skipped" : "failed"})
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {isOrgLevel ? (
                          <span className="text-muted-foreground">-</span>
                        ) : row.totalQuantityChange > 0 ? (
                          <span className="text-emerald-700 dark:text-emerald-400">+{row.totalQuantityChange}</span>
                        ) : row.totalQuantityChange < 0 ? (
                          <span className="text-red-700 dark:text-red-400">{row.totalQuantityChange}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums hidden sm:table-cell">
                        {row.totalCostChangeUsd != null ? (
                          row.totalCostChangeUsd === 0 ? (
                            <span className="text-muted-foreground">{format(0)}</span>
                          ) : (
                            <span className={row.totalCostChangeUsd > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}>
                              {row.totalCostChangeUsd > 0 ? "+" : ""}{format(row.totalCostChangeUsd)}
                            </span>
                          )
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeClass(row.status)}>
                          {formatStatusLabel(row.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {canUndo && locationId ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={isUndoing}
                            onClick={(e) => {
                              e.stopPropagation();
                              setUndoBatchTarget({
                                batchId: row.id,
                                itemCount: row.successfulCount,
                                totalQuantity: row.totalQuantityChange,
                                filename: row.filename,
                                importType: row.importType,
                              });
                            }}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Undo All
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>

                    {expanded ? (
                      <TableRow className="bg-muted/20">
                        <TableCell colSpan={batchColSpan}>
                          {row.importType === "client_price" ? (
                            // With the Clients column the table is wider than
                            // its box at 1280 (about 1030px in 958px). Pin the
                            // change list to the visible width (the cell's 1rem
                            // padding each side) and keep it in view while the
                            // row scrolls, so its Export button and last column
                            // are never cut off.
                            <div className="sticky left-4 w-[calc(100cqw-2rem)]">
                              <ClientPriceChangesRow batchId={row.id} />
                            </div>
                          ) : isBaseGarment && row.locationId !== null ? (
                            // Stock lines first (only stock undoes), then every
                            // other value the upload changed.
                            <div className="space-y-4">
                              <section className="space-y-1">
                                <h4 className="text-xs font-medium text-muted-foreground">
                                  {t("baseGarments.logs.stockTitle")}
                                </h4>
                                {row.rowOrgs.length > 0 ? (
                                  <BulkImportExpandedRow
                                    batchId={row.id}
                                    batchStatus={row.status}
                                    locationId={row.locationId}
                                    organizationId={organizationId}
                                    importType={row.importType}
                                    skuUndo={false}
                                  />
                                ) : (
                                  <NoteLine as="p">{t("baseGarments.logs.noStock")}</NoteLine>
                                )}
                              </section>
                              <section className="space-y-1">
                                <h4 className="text-xs font-medium text-muted-foreground">
                                  {t("baseGarments.logs.changesTitle")}
                                </h4>
                                <div className="sticky left-4 w-[calc(100cqw-2rem)]">
                                  <BaseGarmentChangesRow
                                    batchId={row.id}
                                    orgLabel={ownerOrgLabel ? (id) => ownerOrgLabel(id, null) : undefined}
                                  />
                                </div>
                              </section>
                            </div>
                          ) : row.importType === "sku_edit" || row.locationId === null ? (
                            <div className="sticky left-4 w-[calc(100cqw-2rem)]">
                              <SkuEditChangesRow batchId={row.id} />
                            </div>
                          ) : (
                            <BulkImportExpandedRow
                              batchId={row.id}
                              batchStatus={row.status}
                              locationId={row.locationId}
                              organizationId={organizationId}
                              importType={row.importType}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>

        {pagination && pagination.totalPages > 1 ? (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
              {pagination.total} imports
            </p>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPage((prev) => Math.min(pagination.totalPages, prev + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <UndoConfirmDialog
        open={undoBatchTarget !== null}
        onConfirm={handleUndoBatch}
        onCancel={() => setUndoBatchTarget(null)}
        isLoading={isUndoing}
        title={
          undoBatchTarget?.importType === 'base_garment'
            ? t("baseGarments.logs.undoTitle")
            : undoBatchTarget?.importType === 'stocktake'
              ? "Undo entire stocktake?"
              : "Undo entire bulk import?"
        }
        description={
          undoBatchTarget?.importType === 'base_garment'
            ? t("baseGarments.logs.undoBody")
            : undoBatchTarget?.importType === 'stocktake'
              ? "Each SKU will be reset to its stock level from before the stocktake. If stock has been added or removed since then, those changes will be overwritten."
              : undoBatchTarget?.filename
                ? `This will reverse all stock changes from "${undoBatchTarget.filename}".`
                : "This will reverse all stock changes from this bulk import."
        }
        itemCount={undoBatchTarget?.itemCount ?? 0}
        totalQuantity={undoBatchTarget?.totalQuantity ?? 0}
        warningText={
          undoBatchTarget?.importType === 'base_garment'
            ? t("baseGarments.logs.undoWarning")
            : undoBatchTarget?.importType === 'stocktake'
              ? "Stock levels will be restored to their values before the stocktake. Any changes made since then will be overwritten."
              : undefined
        }
      />
    </div>
  );
};

export default BulkImportsTab;
